import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import {
    ArrowRightIcon
} from '@heroicons/react/24/outline'

const UniversityCard = ({ university, variants }) => {
    const [logoError, setLogoError] = useState(false)
    const hasValidLogo = university.logo?.url && !logoError

    return (
        <motion.div
            variants={variants}
            className="group"
        >
            <Link
                to={`/universities/${university.slug}`}
                className="block bg-white rounded-xl shadow-soft p-6 hover:shadow-lg transition-all duration-300 group-hover:scale-105"
            >
                <div className="flex items-center justify-center h-24">
                    {hasValidLogo ? (
                        <img
                            src={university.logo.url}
                            alt={university.name}
                            className="max-w-full max-h-full object-contain"
                            onError={() => setLogoError(true)}
                        />
                    ) : (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                <span className="text-2xl font-bold text-primary-600">{university.name.charAt(0)}</span>
                            </div>
                            <div className="text-sm text-gray-600 font-medium">{university.name}</div>
                        </div>
                    )}
                </div>
            </Link>
        </motion.div>
    )
}

const FeaturedUniversities = ({ universities = [] }) => {
    if (universities.length === 0) {
        return null
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: 'easeOut',
            },
        },
    }

    return (
        <section className="py-16 lg:py-24 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12 lg:mb-16"
                >
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                        Top <span className="text-accent-500">Universities</span> & <span className="text-primary-600">Colleges</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Partner with India's leading universities offering world-class online and distance learning programs
                    </p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6"
                >
                    {universities.slice(0, 12).map((university, index) => (
                        <UniversityCard
                            key={university._id || university.id}
                            university={university}
                            variants={itemVariants}
                        />
                    ))}
                </motion.div>

                {/* View All Button */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-center mt-12"
                >
                    <Link
                        to="/universities"
                        className="btn-outline btn-lg inline-flex items-center space-x-2"
                    >
                        <span>View All Universities</span>
                        <ArrowRightIcon className="w-5 h-5" />
                    </Link>
                </motion.div>
            </div>
        </section>
    )
}

export default FeaturedUniversities
